import React from 'react';
import PropTypes from 'prop-types';
import { Table } from '@esgian/esgianui';
import { checkUserHasAccess } from '@helpers';
import { useTheme } from '@hooks';
import { useSelector } from 'react-redux';
import { getUser } from '@store/features';
import { formatToDecimalPlaces } from '@helpers';
import { useTimezone } from '@hooks/useTimezone';
import { DRAUGHT_ON_ARRIVAL_DEPARTURE_TEXT } from '@constants/tooltips';
import { TextWithTooltipIcon } from '@components';

function PortWaitingTimeTable({ loading, voyagePortCalls, isOngoingVoyage }) {
  const { theme } = useTheme();
  const user = useSelector(getUser);

  const { getTimeZoneDisplay } = useTimezone();

  const columns = [
    {
      name: 'portName',
      label: 'Port',
      options: {
        sortThirdClickReset: true,
        filterType: 'multiselect'
      }
    },
    {
      name: 'arrivalDraught',
      label: 'Arrival draught',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Arrival draught'}
            tooltipText={DRAUGHT_ON_ARRIVAL_DEPARTURE_TEXT}
            labelVariant={'body2'}
          />
        ),
        sortThirdClickReset: true,
        filterType: 'multiselect',
        customBodyRenderLite: (dataIndex) => {
          const arrivalDraught = voyagePortCalls[dataIndex]?.arrivalDraught;
          const imoDraught = voyagePortCalls[dataIndex]?.imoDraught;
          const percentArrivalDraught = voyagePortCalls[dataIndex]?.percentArrivalDraught;

          const arrivalDraughtFormatted =
            arrivalDraught !== null && arrivalDraught !== undefined
              ? formatToDecimalPlaces(arrivalDraught, 1)
              : '-';
          const imoDraughtFormatted =
            imoDraught !== null && imoDraught !== undefined
              ? formatToDecimalPlaces(imoDraught, 1)
              : '-';
          const percentFormatted =
            percentArrivalDraught !== null && percentArrivalDraught !== undefined
              ? formatToDecimalPlaces(percentArrivalDraught, 1)
              : '-';

          return `${arrivalDraughtFormatted}/${imoDraughtFormatted} (${percentFormatted}%)`;
        }
      }
    },
    {
      name: 'departureDraught',
      label: 'Departure draught',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Departure draught'}
            tooltipText={DRAUGHT_ON_ARRIVAL_DEPARTURE_TEXT}
            labelVariant={'body2'}
          />
        ),
        sortThirdClickReset: true,
        filterType: 'multiselect',
        customBodyRenderLite: (dataIndex) => {
          const departureDraught = voyagePortCalls[dataIndex]?.departureDraught;
          const imoDraught = voyagePortCalls[dataIndex]?.imoDraught;
          const percentDepartureDraught = voyagePortCalls[dataIndex]?.percentDepartureDraught;

          const departureDraughtFormatted =
            departureDraught !== null && departureDraught !== undefined
              ? formatToDecimalPlaces(departureDraught, 1)
              : '-';
          const imoDraughtFormatted =
            imoDraught !== null && imoDraught !== undefined
              ? formatToDecimalPlaces(imoDraught, 1)
              : '-';
          const percentFormatted =
            percentDepartureDraught !== null && percentDepartureDraught !== undefined
              ? formatToDecimalPlaces(percentDepartureDraught, 1)
              : '-';

          return `${departureDraughtFormatted}/${imoDraughtFormatted} (${percentFormatted}%)`;
        }
      }
    },
    {
      name: 'arrivalDate',
      label: 'Arrival date',
      options: {
        sortThirdClickReset: true,
        filterType: 'multiselect',
        customBodyRenderLite: (dataIndex) => {
          const date = voyagePortCalls[dataIndex].arrivalDate;
          return getTimeZoneDisplay(date);
        }
      }
    },
    {
      name: 'departureDate',
      label: 'Departure date',
      options: {
        sortThirdClickReset: true,
        filterType: 'multiselect',
        customBodyRenderLite: (dataIndex) => {
          const date = voyagePortCalls[dataIndex].departureDate;
          return getTimeZoneDisplay(date);
        }
      }
    },
    {
      name: 'portTimeDays',
      label: 'Port time (days)',
      options: {
        sortThirdClickReset: true,
        filterType: 'multiselect',
        customBodyRenderLite: (dataIndex) => {
          const value = voyagePortCalls[dataIndex]?.portTimeDays;
          return formatToDecimalPlaces(value, 2);
        }
      }
    },
    {
      name: 'portWaitingTimeDays',
      label: 'Port waiting time (days)',
      options: {
        sortThirdClickReset: true,
        filterType: 'multiselect',
        customBodyRenderLite: (dataIndex) => {
          const value = voyagePortCalls[dataIndex]?.portWaitingTimeDays;
          return formatToDecimalPlaces(value, 2);
        }
      }
    }
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    filter: true,
    search: true,
    download: checkUserHasAccess(user, true),
    downloadOptions: {
      filename: 'ports-overview.csv'
    },
    setTableProps: () => {
      return {
        id: 'voyage-pwt-table'
      };
    },
    enableNestedDataAccess: '.',
    print: false,
    viewColumns: false,
    pagination: false,
    toolbar: false,
    tableBodyMaxHeight: isOngoingVoyage ? '100%' : '230px',
    rowsPerPageOptions: [10, 25, 50, 100],
    elevation: 0,
    sort: false
  };

  return (
    <Table
      style={{ p: 2, height: isOngoingVoyage ? '700px' : '400px' }}
      skeletonRows={6}
      loading={loading}
      mode={theme.mode}
      title=""
      data={voyagePortCalls ?? []}
      columns={columns}
      options={options}
    />
  );
}

PortWaitingTimeTable.propTypes = {
  loading: PropTypes.bool,
  isOngoingVoyage: PropTypes.bool,
  voyagePortCalls: PropTypes.array
};

PortWaitingTimeTable.defaultProps = {
  loading: false,
  isOngoingVoyage: false,
  voyagePortCalls: []
};

export default PortWaitingTimeTable;
